import { useEffect, useMemo, useState } from 'react';
import { useRefillsStore } from '../../stores/useRefillsStore';
import { usePersonalInfoStore } from '../../stores/usePersonalInfoStore';
import { useBillingStore } from '../../stores/useBillingStore';
import { getSessionUser, type SessionUser } from '../../utils/session';
import { formatCurrency } from '../../utils/formatters';
import { derivePosStatus, type PosStatusUser } from '../../utils/posStatus';
import { RefillsHeader } from './components/RefillsHeader';
import { DeliveryOptions } from './components/DeliveryOptions';
import { RefillsList } from './components/RefillsList';
import { RefillsFooter } from './components/RefillsFooter';
import { ConfirmOrderPanel } from './components/ConfirmOrderPanel';
import { MailOrderWizard } from './components/mail-order/MailOrderWizard';
import { WizardIntroScreen } from './components/mail-order/WizardIntroScreen';
import { MailOrderPendingBanner } from './components/mail-order/MailOrderPendingBanner';
import { RefillDetailModal } from './components/RefillDetailModal';
import { PersonalInfoSection } from './components/sidebar/PersonalInfoSection';
import { AddCreditCardSection } from './components/sidebar/AddCreditCardSection';

const MOBILE_BREAKPOINT = 768;

export interface RefillsPageProps {
  userId: string | null;
  config: {
    customerServicePhone: string;
    customerServiceEmail: string;
  };
  navigate: {
    toMyAccount: (tab?: 'address' | 'creditcard') => void;
    toFeatures: () => void;
  };
}

export function RefillsPage({ userId, config, navigate }: RefillsPageProps) {
  const [enrollmentComplete, setEnrollmentComplete] = useState(false);
  const [introComplete, setIntroComplete] = useState(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: getSessionUser is a stable imported function, not a reactive dependency
  const user = useMemo(() => getSessionUser<SessionUser>(), [enrollmentComplete]);
  const posStatus = useMemo(() => derivePosStatus(user as PosStatusUser | null), [user]);
  const canChangeCreditCard = useMemo(() => {
    const features = (user?.Features as Array<{ FeatureId?: number }> | undefined) || [];
    return features.some(f => f && f.FeatureId === 29);
  }, [user]);
  const {
    loading,
    noScripts,
    error,
    confirmUserName,
    selectedRefills,
    refills,
    countPrice,
    isMailOrder,
    isPatientAutoRefillAllowed,
    isPatientAutoRefillEnabled,
    loadRefills,
    prepareConfirmOrder,
  } = useRefillsStore();

  const [view, setView] = useState<'List' | 'Grid'>(
    window.innerWidth <= MOBILE_BREAKPOINT ? 'Grid' : 'List'
  );
  const [showConfirmOrder, setShowConfirmOrder] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [showPersonalInfo, setShowPersonalInfo] = useState(false);
  const [showCreditCardForm, setShowCreditCardForm] = useState(false);
  const [pendingSidebarNav, setPendingSidebarNav] = useState<(() => void) | null>(null);

  const closeCreditCardForm = () => {
    useBillingStore.getState().clearForm();
    setShowCreditCardForm(false);
  };

  const guardedSidebarNav = (action: () => void) => {
    const isDirty =
      (showPersonalInfo && usePersonalInfoStore.getState().isDirty()) ||
      (showCreditCardForm && useBillingStore.getState().isDirty());

    if (isDirty) {
      setPendingSidebarNav(() => action);
      return;
    }

    action();
  };

  const confirmSidebarNav = () => {
    pendingSidebarNav?.();
    setPendingSidebarNav(null);
  };

  const cancelSidebarNav = () => setPendingSidebarNav(null);

  // Derive initial linkToken from userId prop or first eligible user
  const initialLinkToken = useMemo(() => {
    if (userId) return userId;
    if (user?.LinkToken) return String(user.LinkToken);
    return '';
  }, [userId, user]);

  const [currentLinkToken, setCurrentLinkToken] = useState(initialLinkToken);

  useEffect(() => {
    if (currentLinkToken) {
      loadRefills(currentLinkToken);
    }
  }, [currentLinkToken, loadRefills]);

  // Auto-dismiss the Review Order validation toast after ~5s (matches legacy
  // alertify.error auto-hide timing — VSDP-5997).
  useEffect(() => {
    if (!toastMessage) return;
    const timer = setTimeout(() => setToastMessage(null), 5000);
    return () => clearTimeout(timer);
  }, [toastMessage]);

  const handleUserChange = (linkToken: string) => {
    setCurrentLinkToken(linkToken);
    setShowConfirmOrder(false);
    setShowPersonalInfo(false);
    closeCreditCardForm();
  };

  const handleReviewOrder = () => {
    prepareConfirmOrder();
    const storeError = useRefillsStore.getState().error;
    if (storeError) {
      // Surface the validation (e.g. missing home-delivery shipping address) as a
      // transient top-right toast for ~5s, matching production's legacy alertify.error
      // behavior (VSDP-5997). Clear the store error so it isn't also left in the
      // persistent inline banner.
      setToastMessage(storeError);
      useRefillsStore.getState().clearError();
      return;
    }
    setShowConfirmOrder(true);
  };

  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailSelectedRx, setDetailSelectedRx] = useState<string | null>(null);

  const handleShowDetail = (rx: string) => {
    setDetailSelectedRx(rx);
    setDetailModalOpen(true);
  };

  const hasSelected = selectedRefills.length > 0;

  // Three-branch enrollment gate:
  //   1. isEnrolled (IsInGuardian !== false) → standard refills experience (safe default)
  //   2. not enrolled + pending (IsMailOrderActivationPending === true) → pending banner
  //   3. not enrolled + not pending + not complete → intro screen → wizard
  const isEnrolled = user?.IsInGuardian !== false; // null/undefined → enrolled (safe default)
  const isPending = user?.IsMailOrderActivationPending === true;
  const showPendingBanner = !isEnrolled && isPending;
  const showWizard = !isEnrolled && !isPending && !enrollmentComplete;

  if (showPendingBanner) {
    return (
      <div className="col-md-12">
        <MailOrderPendingBanner customerServicePhone={config.customerServicePhone} />
      </div>
    );
  }

  if (showWizard && !introComplete) {
    return (
      <div className="col-md-12">
        <WizardIntroScreen
          customerServicePhone={config.customerServicePhone}
          onBegin={() => setIntroComplete(true)}
        />
      </div>
    );
  }

  if (showWizard) {
    return (
      <div className="col-md-12">
        <div className="panel">
          <div className="panel-body">
            <MailOrderWizard
              customerServicePhone={config.customerServicePhone}
              onEnrollmentComplete={() => setEnrollmentComplete(true)}
              onCancel={() => {
                // Cancelling must not fall through to the same default view shown after a
                // successful enrollment — per confirmed product decision, exit to the
                // features/tiles landing page instead (VSDP-5964).
                navigate.toFeatures();
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Transient Review Order validation toast — top-right, auto-dismisses after ~5s.
          Replicates the legacy alertify.error toast (red top-right log, styled by
          .alertify-logs / .alertify-log-error) inline as a controlled, testable React
          element rather than calling the global alertify library, matching the same
          approach used for the unsaved-changes dialog below (VSDP-5997). */}
      {toastMessage && (
        <div
          role="alert"
          data-testid="review-order-toast"
          style={{
            position: 'fixed',
            top: 10,
            right: 10,
            width: 300,
            zIndex: 5000,
            padding: 15,
            borderRadius: 4,
            background: 'rgba(254, 26, 0, 0.9)',
            color: '#FFF',
            boxShadow: '0 3px 3px rgba(0, 0, 0, .3)',
          }}
        >
          {toastMessage}
        </div>
      )}

      {/* Sidebar */}
      <div className="col-md-3">
        <div className="panel">
          <div className="panel-body">
            <RefillsHeader
              currentLinkToken={currentLinkToken}
              onUserChange={handleUserChange}
              view={view}
              onViewChange={setView}
              loading={loading}
            />
            <DeliveryOptions loading={loading} />
            {isPatientAutoRefillAllowed && currentLinkToken && !loading && (
              <div className="activate-autorefill-housing">
                <div className="activate-autorefill-btn">
                  <a href={`/#/auto-refill/${currentLinkToken}`} style={{ outline: 'none' }}>
                    {isPatientAutoRefillEnabled ? 'Manage Auto Refill' : 'Activate Auto Refill'}
                  </a>
                </div>
              </div>
            )}

            {/* Personal Info nav link — hidden while refills are loading */}
            {!loading && (
              <div className="activate-autorefill-housing" style={{ marginTop: 8 }}>
                <div className="activate-autorefill-btn">
                  {/** biome-ignore lint/a11y/useValidAnchor: <a> is used for navigation within the app */}
                  <a
                    href="#"
                    onClick={e => {
                      e.preventDefault();
                      guardedSidebarNav(() => {
                        setShowPersonalInfo(prev => !prev);
                        if (showCreditCardForm) {
                          closeCreditCardForm();
                        }
                      });
                    }}
                    className="refillLeftNavLink"
                  >
                    Personal Info
                  </a>
                </div>
              </div>
            )}

            {/* Add/Update Credit Card nav link — hidden while refills are loading */}
            {!loading && canChangeCreditCard && (
              <div className="activate-autorefill-housing" style={{ marginTop: 8 }}>
                <div className="activate-autorefill-btn">
                  {/** biome-ignore lint/a11y/useValidAnchor: <a> is used for navigation within the app */}
                  <a
                    href="#"
                    onClick={e => {
                      e.preventDefault();
                      guardedSidebarNav(() => {
                        setShowCreditCardForm(prev => {
                          const next = !prev;
                          if (!next) {
                            useBillingStore.getState().clearForm();
                          }

                          return next;
                        });
                        setShowPersonalInfo(false);
                      });
                    }}
                    className="refillLeftNavLink"
                  >
                    Add/Update Credit Card
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="col-md-9">
        {showPersonalInfo ? (
          <div className="panel">
            <div className="panel-heading panel-default refill-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0 }}>
                Edit Account <small style={{ color: '#777' }}>Personal Information</small>
              </h3>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => guardedSidebarNav(() => setShowPersonalInfo(false))}
              >
                &larr; Back to Refills
              </button>
            </div>
            <div className="panel-body">
              <PersonalInfoSection
                customerServicePhone={config.customerServicePhone}
                onClose={() => guardedSidebarNav(() => setShowPersonalInfo(false))}
              />
            </div>
          </div>
        ) : showCreditCardForm ? (
          <div className="panel">
            <div className="panel-heading panel-default refill-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0 }}>
                Edit Account <small style={{ color: '#777' }}>Credit Card Information</small>
              </h3>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => guardedSidebarNav(closeCreditCardForm)}
              >
                &larr; Back to Refills
              </button>
            </div>
            <div className="panel-body">
              <AddCreditCardSection
                customerServicePhone={config.customerServicePhone} 
                customerServiceEmail={config.customerServiceEmail}
                onClose={() => guardedSidebarNav(closeCreditCardForm)}
              />
            </div>
          </div>
        ) : (
        <div className="panel">
          {/* Panel Heading */}
          <div className="panel-heading panel-default refill-title">
            <div className="refill-title-panel-container">
              <h3 className="refill-title">
                <strong>My Refills</strong>
              </h3>
              {!loading && <div className="refill-username">{confirmUserName}</div>}
            </div>

            {!loading && (
              <div className="charge-details-container">
                <div className={`charge-details-inner ${posStatus.PosStatusClass}`}>
                  <h5 className="pos-display">
                    {posStatus.IsSecondary && <i className="material-icons">info</i>}
                    {posStatus.BalanceGood &&
                      !posStatus.HasCreditCard &&
                      !posStatus.IsSecondary && <i className="material-icons">check_circle</i>}
                    {!posStatus.BalanceGood &&
                      !posStatus.HasCreditCard &&
                      !posStatus.IsSecondary && <i className="material-icons">error</i>}
                    {posStatus.StatusText}{' '}
                    {!posStatus.HasCreditCard && posStatus.HasPos && !posStatus.ShowNegative && (
                      <span>{formatCurrency(posStatus.AccountBalance)}</span>
                    )}
                    {!posStatus.HasCreditCard && posStatus.HasPos && posStatus.ShowNegative && (
                      <span>{formatCurrency(posStatus.AccountBalance * -1)}</span>
                    )}

                    {posStatus.HasSpecialInsurance && !posStatus.HasCreditCard && (
                      <div
                        className={`credit-limit-no-first-fill ${posStatus.AvailableCreditClass}`}
                      >
                        Credit Limit Remaining: {formatCurrency(posStatus.AvailableCredit)}
                      </div>
                    )}
                    {posStatus.HasCreditCard && posStatus.AvailableCredit !== 0 && (
                      <h6
                        className={`credit-card-account-balance ${posStatus.AvailableCreditClass}`}
                      >
                        Account Balance: {formatCurrency(posStatus.AvailableCredit)}
                      </h6>
                    )}
                  </h5>
                </div>

                {hasSelected ? (
                  <div className="tally-total">
                    <i className="material-icons">shopping_cart</i>
                    <h6>
                      Order Total: <strong>{formatCurrency(countPrice)} *</strong>
                    </h6>
                  </div>
                ) : (
                  <div className="tally-total">
                    <i className="material-icons">shopping_cart</i>
                    <h6 className="no-items-selected">No Items Selected</h6>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="clearfix" />

          {/* Error banner */}
          {error && (
            <div className="alert alert-danger" style={{ margin: 10 }}>
              {error}
            </div>
          )}

          {/* Confirm order modal (portaled to body when open) */}
          <RefillsList view={view} onShowDetail={handleShowDetail} />
          <RefillsFooter onReviewOrder={handleReviewOrder} />
          {showConfirmOrder && (
            <ConfirmOrderPanel
              view={view}
              customerServicePhone={config.customerServicePhone}
              onClose={() => setShowConfirmOrder(false)}
            />
          )}
        </div>
        )}
      </div>

      {/* Refill detail modal */}
      <RefillDetailModal
        isOpen={detailModalOpen}
        refills={refills}
        selectedRx={detailSelectedRx}
        onClose={() => setDetailModalOpen(false)}
      />

      {/* Unsaved-changes confirmation dialog — Personal Info / Add-Update Credit Card sidebar sections.
          Matches production's actual dialog exactly: prod renders this via the legacy
          alertify.confirm() call, styled by nav.alertify-buttons / .alertify-button-ok /
          #alertify-cancel in style.css + the brand overrides in style-maxorplus.css
          (centered title/body, centered pill buttons in the app's brand orange/yellow,
          and a 5px brand-orange bar under the button row). Replicated inline here rather
          than calling the global alertify library so the dialog stays a controlled,
          testable React component. */}
      {pendingSidebarNav && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            height: '100%',
            width: '100%',
            background: 'rgba(0, 0, 0, 0.7)',
            zIndex: 1040,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="unsaved-sidebar-dialog-title"
          onKeyDown={e => {
            if (e.key === 'Escape') {
              cancelSidebarNav();
            }
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 8,
              boxShadow: '0 3px 3px rgba(0, 0, 0, .3)',
              padding: 25,
              width: 550,
              maxWidth: '90%',
              textAlign: 'center',
            }}
          >
            <h3
              id="unsaved-sidebar-dialog-title"
              style={{ textTransform: 'uppercase', fontWeight: 'bold', marginTop: 0 }}
            >
              Unsaved Changes
            </h3>
            <p style={{ marginBottom: 15 }}>
              The form contains unsaved changes, are you sure you want to leave this screen?
            </p>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: 10,
                paddingBottom: 15,
                borderBottom: '5px solid #FF471A',
              }}
            >
              <button
                type="button"
                style={{
                  backgroundColor: '#FF471A',
                  color: '#FAFAFA',
                  border: 'none',
                  borderRadius: 4,
                  padding: '8px 20px',
                  cursor: 'pointer',
                }}
                onClick={cancelSidebarNav}
              >
                Cancel
              </button>
              <button
                type="button"
                style={{
                  backgroundColor: '#FFD24C',
                  color: '#1E1E1E',
                  border: 'none',
                  borderRadius: 4,
                  padding: '8px 20px',
                  minWidth: 75,
                  cursor: 'pointer',
                }}
                onClick={confirmSidebarNav}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
